import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { TrendingUp, ShoppingCart, Users, ShoppingBag, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminOverview() {
  const [stats, setStats] = useState({ total_orders: 0, total_users: 0, total_products: 0, total_products_sold: 0, revenue: 0 });
  const [recent, setRecent] = useState([]);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/orders").then((r) => setRecent(r.data.slice(0, 5)));
  }, []);

  const runDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const r = await api.get("/admin/diagnostics");
      setDiag(r.data);
      if (r.data?.all_ok) {
        toast.success("All systems healthy");
      } else {
        toast.error("One or more components are unhealthy — see details below");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDiagLoading(false);
    }
  };

  // iter53 — fourth card is now "Total Products Sold" (sum of item
  // quantities across DELIVERED orders) instead of "Products" (catalog size).
  const cards = [
    { label: "Revenue", value: `₹${stats.revenue.toFixed(0)}`, icon: TrendingUp, testid: "stat-revenue" },
    { label: "Orders", value: stats.total_orders, icon: ShoppingBag, testid: "stat-orders" },
    { label: "Customers", value: stats.total_users, icon: Users, testid: "stat-customers" },
    { label: "Total Products Sold", value: stats.total_products_sold ?? 0, icon: ShoppingCart, testid: "stat-products-sold" },
  ];

  const fmtUptime = (s) => {
    if (s == null) return "—";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m ${s % 60}s`;
  };

  return (
    <div data-testid="admin-overview">
      <h1 className="font-serif text-4xl text-brand-dark mb-8">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-brand-line rounded-xl p-5" data-testid={c.testid}>
            <c.icon className="w-5 h-5 text-brand-chili mb-3" strokeWidth={1.5} />
            <div className="text-2xl font-serif text-brand-dark font-semibold">{c.value}</div>
            <div className="text-xs uppercase tracking-wider text-brand-muted mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      {/* iter62 — Run-time backend diagnostics. Click to verify every
         backing service (Mongo, GridFS, Cloudinary, Razorpay, Shiprocket)
         is healthy. Surfaces the actual error from any failing component
         right inside the dashboard. */}
      <div className="bg-white border border-brand-line rounded-2xl p-5 mb-10" data-testid="diagnostics-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-brand-chili" strokeWidth={1.5} />
            <h2 className="font-serif text-xl text-brand-dark">Backend health</h2>
          </div>
          <button
            type="button"
            onClick={runDiagnostics}
            disabled={diagLoading}
            data-testid="run-diagnostics-btn"
            className="inline-flex items-center gap-2 text-sm rounded-full border border-brand-line px-4 py-1.5 hover:bg-brand-subtle disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${diagLoading ? "animate-spin" : ""}`} strokeWidth={1.8} />
            {diagLoading ? "Checking…" : "Run diagnostics"}
          </button>
        </div>
        {diag ? (
          <div data-testid="diagnostics-results">
            <div className="text-xs text-brand-muted mb-3">
              Process uptime: <span className="font-mono">{fmtUptime(diag.uptime_seconds)}</span> · Checked at <span className="font-mono">{new Date(diag.now).toLocaleTimeString()}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(diag.checks || {}).map(([name, info]) => (
                <div
                  key={name}
                  data-testid={`diag-${name}`}
                  className={`border rounded-lg p-3 text-sm ${info.ok ? "border-emerald-200 bg-emerald-50" : "border-red-300 bg-red-50"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium capitalize">{name}</span>
                    <span className={`text-xs uppercase tracking-wider ${info.ok ? "text-emerald-700" : "text-red-700"}`}>
                      {info.ok ? "OK" : "FAIL"}
                    </span>
                  </div>
                  <div className="text-xs text-brand-muted mt-1">{info.ms} ms</div>
                  {!info.ok && (
                    <div className="text-xs text-red-700 mt-2 break-words font-mono">
                      {info.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-brand-muted">
            Click <em>Run diagnostics</em> to probe every backing service. Use this immediately if any admin action returns
            <em> Something went wrong on our end</em> — the failing component (and its error) will show up here.
          </p>
        )}
      </div>

      <h2 className="font-serif text-2xl text-brand-dark mb-4">Recent orders</h2>
      <div className="bg-white border border-brand-line rounded-2xl overflow-hidden">
        {recent.length === 0 ? (
          <div className="p-8 text-center text-brand-muted text-sm" data-testid="recent-orders-empty">No Orders</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-brand-subtle text-xs uppercase tracking-wider text-brand-muted">
              <tr>
                <th className="text-left p-4">Order</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4">Total</th>
                <th className="text-left p-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((o) => (
                <tr key={o.order_id} className="border-t border-brand-line">
                  <td className="p-4 font-mono text-xs">{o.order_id}</td>
                  <td className="p-4">{o.user_name}</td>
                  <td className="p-4 font-medium">₹{o.total.toFixed(0)}</td>
                  <td className="p-4"><span className="text-xs uppercase tracking-wider px-2 py-1 bg-brand-subtle rounded-full">{o.order_status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
